import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Variant-reactive product fields that Horizon does not ship out of the box.
 *
 * Both components listen for the same product select event as the SKU block.
 * The GTIN reads the barcode straight off the variant JSON that the event
 * carries. The metafield block cannot do that — metafields are not part of the
 * variant JSON — so it re-reads its own markup from the freshly fetched page
 * that the event exposes as `detail.html`.
 */

const TARGET_SELECTOR = '[id*="ProductInformation-"], [id*="QuickAdd-"], product-card';

/**
 * @typedef {Object} GtinRefs
 * @property {HTMLElement} gtinContainer - The container element for the GTIN
 * @property {HTMLElement} gtin - The span element that displays the GTIN text
 *
 * @extends {Component<GtinRefs>}
 */
class ProductGtinComponent extends Component {
  requiredRefs = ['gtinContainer', 'gtin'];

  connectedCallback() {
    super.connectedCallback();
    this.closest(TARGET_SELECTOR)?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest(TARGET_SELECTOR)?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the GTIN.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (!resource) return;

        const barcode = resource.barcode || '';

        this.style.display = barcode ? 'block' : 'none';
        this.refs.gtin.textContent = barcode;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-gtin] Event promise rejected:', error);
      });
  };
}

/**
 * Displays a variant metafield. Metafields are absent from the variant JSON, so
 * the markup is copied from the re-rendered page that the event carries.
 */
class ProductVariantMetafieldComponent extends HTMLElement {
  connectedCallback() {
    this.closest(TARGET_SELECTOR)?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.closest(TARGET_SELECTOR)?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, html } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const blockId = this.dataset.blockId;
        if (!html || !blockId) return;

        // Quick add and product cards are rendered from a different section, so
        // the counterpart is simply absent there. Leave the current value alone.
        const next = html.querySelector(
          `product-variant-metafield-component[data-block-id="${CSS.escape(blockId)}"]`
        );
        if (!next) return;

        this.innerHTML = next.innerHTML;
        this.style.display = /** @type {HTMLElement} */ (next).style.display || 'block';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-variant-metafield] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('product-gtin-component')) {
  customElements.define('product-gtin-component', ProductGtinComponent);
}

if (!customElements.get('product-variant-metafield-component')) {
  customElements.define('product-variant-metafield-component', ProductVariantMetafieldComponent);
}
